import { useEffect, useRef, useContext } from "react";
import * as _ from "lodash-es";
import EndpointsEnum from "@models/EndpointsEnum";
import IOnGetCatalogItemsDTO from "@models/DTO/IOnGetCatalogItemsDTO";
import IOnGetPriceGroupsDTO from "@models/DTO/IOnGetPriceGroupsDTO";
import IOnElasticSearchSyncCatalogDTO from "@models/DTO/IOnElasticSearchSyncCatalogDTO";
import IOnQBDCreateCatalogItemsDTO from "@models/DTO/IOnQBDCreateCatalogItemsDTO";
import IOnQBDPushItemsDTO from "@models/DTO/IOnQBDPushItemsDTO";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { CatalogOverviewState } from "./useCatalogOverview";
import IGetCatalogDTO from "@models/DTO/IGetCatalogDTO";
import IGetPriceGroupsDTO from "@models/DTO/IGetPriceGroupsDTO";
import IGetCatalogItemsDTO from "@models/DTO/IGetCatalogItemsDTO";
import ICatalogOnCreatePriceGroupDTO from "@models/DTO/ICatalogOnCreatePriceGroupDTO";
import ICatalogOnUpdatePriceGroupDTO from "@models/DTO/ICatalogOnUpdatePriceGroupDTO";

interface UseSocketListenersProps {
    catalogGuid: string;
    setState: (updates: Partial<CatalogOverviewState>) => void;
    handlers: {
        onGetCatalogItems: (data: IOnGetCatalogItemsDTO) => void;
        onGetPriceGroups: (data: IOnGetPriceGroupsDTO) => void;
        onGetCatalog: (data: any) => void;
        onElasticSearchSyncCatalog: (data: IOnElasticSearchSyncCatalogDTO) => void;
        onQBDCreateCatalogItems: (data: IOnQBDCreateCatalogItemsDTO) => void;
        onQBDPushItems: (data: IOnQBDPushItemsDTO) => void;
        onCreatePriceGroup: (data: ICatalogOnCreatePriceGroupDTO) => void;
        onUpdatePriceGroup: (data: ICatalogOnUpdatePriceGroupDTO) => void;
    };
}

export const useCatalogSocketListeners = ({ catalogGuid, setState, handlers }: UseSocketListenersProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const isMounted = useRef(true);

    const getCatalog = () => {
        const payload: IGetCatalogDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
        };
        socket.emit(EndpointsEnum.CatalogsGetCatalog, payload);
    };

    const getPriceGroups = () => {
        const payload: IGetPriceGroupsDTO = {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            catalogGuid,
        };

        socket.emit(EndpointsEnum.CatalogsGetPriceGroups, payload);
    };

    const getCatalogItems = (options: { queryString?: string; skipTotalCount?: boolean; limit?: number; offset?: number }) => {
        const data: IGetCatalogItemsDTO = {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            catalogGuid,
            queryString: options.queryString,
            limit: options.limit,
            offset: options.offset,
            skipTotalCount: options.skipTotalCount,
        };

        socket.emit(EndpointsEnum.CatalogItemGetCatalogItems, data);

        setState({ isDataFetching: true });
    };

    useEffect(() => {
        const endpoints = [
            { endpoint: EndpointsEnum.CatalogItemOnGetCatalogItems, fn: handlers.onGetCatalogItems },
            { endpoint: EndpointsEnum.CatalogsOnGetPriceGroups, fn: handlers.onGetPriceGroups },
            { endpoint: EndpointsEnum.CatalogsOnGetCatalog, fn: handlers.onGetCatalog },
            { endpoint: EndpointsEnum.ElasticSearchOnSyncCatalog, fn: handlers.onElasticSearchSyncCatalog },
            { endpoint: EndpointsEnum.QBDOnCreateCatalogItems, fn: handlers.onQBDCreateCatalogItems },
            { endpoint: EndpointsEnum.QBDOnPushItems, fn: handlers.onQBDPushItems },
            { endpoint: EndpointsEnum.CatalogsOnCreatePriceGroup, fn: handlers.onCreatePriceGroup },
            { endpoint: EndpointsEnum.CatalogsOnUpdatePriceGroup, fn: handlers.onUpdatePriceGroup },
        ];

        endpoints.forEach(({ endpoint, fn }) => {
            socket.on(endpoint, fn);
        });

        return () => {
            isMounted.current = false;
            endpoints.forEach(({ endpoint, fn }) => {
                socket.removeListener(endpoint, fn);
            });
        };
    }, [socket]);

    return {
        getCatalog,
        getPriceGroups,
        getCatalogItems,
    };
};
